// Game audio — sound groups, material-based footsteps, event processing.

import { AudioSystem } from "../engine/audio.js";
import { FP } from "../engine/fixed.js";
import { AudioEventType } from "../engine/events.js";

export const audio = new AudioSystem();
audio.setVolume("sfx", 0.7);

// --- Sound loading ---

const STEP_INTERIOR = ["step_int_1", "step_int_2", "step_int_3"];
const STEP_EXTERIOR = ["step_ext_1", "step_ext_2", "step_ext_3"];
const STEP_GROUPS = [STEP_INTERIOR, STEP_EXTERIOR]; // indexed by material

audio.load("step_int_1", "/sounds/Footsteps_Interior_01.ogg");
audio.load("step_int_2", "/sounds/Footsteps_Interior_02.ogg");
audio.load("step_int_3", "/sounds/Footsteps_Interior_03.ogg");
audio.load("step_ext_1", "/sounds/Footsteps_Exterior_01.ogg");
audio.load("step_ext_2", "/sounds/Footsteps_Exterior_02.ogg");
audio.load("step_ext_3", "/sounds/Footsteps_Exterior_03.ogg");
audio.load("land", "/sounds/Footsteps_Land_01.ogg");

function randomFrom(group: string[]): string {
  return group[(Math.random() * group.length) | 0];
}

// --- Event processing ---

const INTS_PER_ENTITY = 7;
let lastTick = -1;

export function processAudioEvents(
  buffer: Int32Array,
  entityCount: number,
  localPlayerId: number,
): void {
  const tick = buffer[0];
  if (tick === lastTick) return;
  lastTick = tick;

  // Skip past sim events to reach audio events
  let off = 2 + entityCount * INTS_PER_ENTITY;
  if (off >= buffer.length) return;
  const simEventCount = buffer[off];
  off += 1 + simEventCount * 5;
  if (off >= buffer.length) return;

  const audioEventCount = buffer[off];
  off++;

  for (let i = 0; i < audioEventCount; i++) {
    const type = buffer[off];
    const entityId = buffer[off + 1];
    const px = FP.toFloat(buffer[off + 2]);
    const py = FP.toFloat(buffer[off + 3]);
    const pz = FP.toFloat(buffer[off + 4]);
    const material = buffer[off + 5];
    off += 6;

    const isLocal = entityId === localPlayerId;
    const pos = isLocal ? undefined : { x: px, y: py, z: pz };

    if (type === AudioEventType.LAND) {
      audio.play("land", pos, {
        volume: isLocal ? 0.35 : 0.55,
        rate: 0.9 + Math.random() * 0.2,
        maxDistance: 40,
      });
    } else if (type === AudioEventType.FOOTSTEP) {
      const group = STEP_GROUPS[material] ?? STEP_GROUPS[0];
      audio.play(randomFrom(group), pos, {
        volume: isLocal ? 0.25 : 0.5,
        rate: 0.85 + Math.random() * 0.3,
        maxDistance: 30,
      });
    }
  }
}

export function updateListener(x: number, y: number, z: number, yaw: number, pitch: number): void {
  audio.setListener(x, y, z, yaw, pitch);
}
